import React, { useEffect, useRef, useState } from "react";
import { Button, message, Tooltip } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { normalizeSalesReturnStatus } from "../../utils/salesReturnEligibility";

type DocumentKind = "credit-note" | "summary";
const idOf = (value: any) => Number(value?.id ?? value) || null;
export const canPrintSalesReturnDocuments = (hasPermission: (permission: string) => boolean) =>
  hasPermission("Gestion Commerciale.Ventes.Ventes.view") &&
  hasPermission("Gestion Commerciale.Ventes.RetourVente.view");

export const getSalesReturnDocumentDisabledReason = (order: any, kind: DocumentKind): string | undefined => {
  if (kind === "credit-note" && ["annule", "annulee", "cancelled", "canceled"].includes(normalizeSalesReturnStatus(order?.order_status))) {
    return "Ce retour est annulé : l'impression de l'avoir est indisponible.";
  }
  if (kind === "summary") {
    if ([false, 0, "0"].includes(order?.has_sales_returns)) {
      return "Cette facture ne comporte aucun retour à récapituler (hors retours annulés ou supprimés).";
    }
    if (![true, 1, "1"].includes(order?.has_sales_returns)) {
      return "Actualisez la facture pour vérifier les retours disponibles.";
    }
  }
  return undefined;
};

export function useSalesReturnDocument() {
  const { user, hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);
  const scope = JSON.stringify([user?.id, companyId, warehouseId]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const pending = useRef<{ controller: AbortController; preview: Window | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const canPrint = canPrintSalesReturnDocuments(hasPermission);

  useEffect(() => {
    setLoading(false);
    return () => {
      pending.current?.controller.abort();
      pending.current?.preview?.close();
      pending.current = null;
    };
  }, [scope]);

  const print = async (order: any, kind: DocumentKind) => {
    if (pending.current) return;
    if (!canPrint || !companyId || !warehouseId || !Number.isSafeInteger(Number(order?.id)) || Number(order.id) <= 0 ||
        order?.__offline || (order?.company_id && Number(order.company_id) !== companyId) ||
        (order?.warehouse_id && Number(order.warehouse_id) !== warehouseId)) {
      message.error("Sélectionnez l'entreprise et l'établissement de ce document et vérifiez vos droits de consultation.");
      return;
    }
    const disabledReason = getSalesReturnDocumentDisabledReason(order, kind);
    if (disabledReason) {
      message.error(disabledReason);
      return;
    }
    const requestScope = scope;
    const controller = new AbortController();
    // Open during the click, before the request, to support browser popup rules.
    const preview = window.open("", "_blank");
    if (preview) {
      preview.opener = null;
      preview.document.title = "Préparation du document";
      preview.document.body.textContent = "Préparation du document…";
    }
    pending.current = { controller, preview };
    setLoading(true);
    try {
      const response = await apiClient.get(API_CONFIG.apiUrl(`/orders/${order.id}/sales-return-document/${kind}`), {
        params: { company_id: companyId, warehouse_id: warehouseId },
        headers: { "X-Company-Id": String(companyId) }, responseType: "blob", timeout: 60000, signal: controller.signal,
      });
      if (controller.signal.aborted || currentScope.current !== requestScope) return;
      const type = String(response.headers?.["content-type"] || "");
      if (!type.includes("application/pdf") || !response.data?.size) throw new Error("Le serveur n'a pas retourné un PDF valide.");
      const url = URL.createObjectURL(response.data);
      if (preview && !preview.closed) {
        preview.location.replace(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${kind === "credit-note" ? "Avoir" : "Recapitulatif-retours"}-${String(order.invoice_number || order.id).replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      // The opened PDF belongs to the user once delivered; later unmounts must
      // not close it. Only an unfinished request is cancelled on scope changes.
    } catch (error: any) {
      preview?.close();
      if (!controller.signal.aborted && currentScope.current === requestScope) {
        let detail = error.message || "Impossible de préparer le document. Réessayez.";
        const body = error.response?.data;
        if (body && typeof body.text === "function") {
          try { detail = JSON.parse(await body.text()).message || detail; } catch { /* Keep the network error. */ }
        }
        if (!controller.signal.aborted && currentScope.current === requestScope) message.error(detail);
      }
    } finally {
      if (pending.current?.controller === controller) {
        pending.current = null;
        setLoading(false);
      }
    }
  };
  return { print, loading, canPrint };
}

export default function SalesReturnDocumentAction({ order, kind, block = false }: {
  order: any; kind: DocumentKind; block?: boolean;
}) {
  const { print, loading, canPrint } = useSalesReturnDocument();
  if (!canPrint || !order || order.__offline || order.record_source === "ecommerce" || Number(order.is_deleted) === 1) return null;
  const disabledReason = getSalesReturnDocumentDisabledReason(order, kind);
  const button = <Button block={block} disabled={Boolean(disabledReason)} title={disabledReason}
    style={block ? { minHeight: 44, height: "auto", whiteSpace: "normal" } : undefined}
    icon={<PrinterOutlined />} loading={loading} onClick={() => void print(order, kind)}>
    {kind === "credit-note" ? "Imprimer l'avoir" : "Imprimer le récapitulatif après retours"}
  </Button>;
  return disabledReason ? <Tooltip title={disabledReason}>
    <span style={{ display: block ? "block" : "inline-block" }}>{button}</span>
  </Tooltip> : button;
}
